import type { FC, FormEvent, ReactNode } from 'react'
import type { DemoField, DemoInput, FieldErrors } from '@/lib/validation'

import Link from 'next/link'
import { useRouter } from 'next/router'
import { useEffect, useRef, useState } from 'react'
import { ArrowLeft, CalendarDays, CircleCheck, Mail, Video } from 'lucide-react'
import { cn } from '@/lib/utils'
import { DEMO_STEPS, validateDemo } from '@/lib/validation'
import { focusFirstError, postJson } from '@/lib/submit'
import { Button } from '@/components/generals/button'

import {
    ChipGroup,
    FormAlert,
    Honeypot,
    SelectField,
    TextAreaField,
    TextField
} from '@/components/forms/fields'

import {
    COMPANY_SIZES,
    CURRENT_TOOLS,
    INDUSTRIES,
    MEETING_TIMES,
    PLAN_INTERESTS
} from '@/constants/forms'

const EMPTY: DemoInput = {
    firstName: '',
    lastName: '',
    email: '',
    jobTitle: '',
    company: '',
    companySize: '',
    industry: '',
    tools: [],
    goals: '',
    planInterest: 'Not sure yet',
    preferredTime: 'Any time works'
}

const STEP_LABELS = ['About you', 'Your company', 'Your goals']

const NEXT_STEPS = [
    { title: 'We review your details', body: 'A finance specialist looks at your stack and goals, usually within one business day.', Icon: Mail },
    { title: 'You pick a time', body: 'We email you a few time options that match your preferred window.', Icon: CalendarDays },
    { title: 'A tailored walkthrough', body: '30 minutes on video, built around your accounts, reports and questions.', Icon: Video }
]

export const DemoForm: FC = (): ReactNode => {
    const router = useRouter()
    const [step, setStep] = useState(0)
    const [values, setValues] = useState<DemoInput>(EMPTY)
    const [errors, setErrors] = useState<FieldErrors<DemoField>>({})
    const [status, setStatus] = useState<'idle' | 'submitting' | 'success'>('idle')
    const [message, setMessage] = useState<string | null>(null)
    const [website, setWebsite] = useState('')
    const headingRef = useRef<HTMLHeadingElement>(null)
    const firstRender = useRef(true)

    useEffect(() => {
        if (!router.isReady) return

        const plan = typeof router.query.plan === 'string' ? router.query.plan.toLowerCase() : ''
        const match = PLAN_INTERESTS.find(option => option.toLowerCase() === plan)

        if (match) setValues(current => ({ ...current, planInterest: match }))
    }, [router.isReady, router.query.plan])

    useEffect(() => {
        if (firstRender.current) {
            firstRender.current = false

            return
        }

        headingRef.current?.focus()
    }, [step, status])

    const update = <K extends DemoField>(field: K, value: DemoInput[K]) => {
        setValues(current => ({ ...current, [field]: value }))

        if (errors[field]) setErrors(current => ({ ...current, [field]: undefined }))
    }

    const errorsFor = (fields: Array<DemoField>): FieldErrors<DemoField> => {
        const all = validateDemo({
            ...values,
            firstName: values.firstName.trim(),
            lastName: values.lastName.trim(),
            email: values.email.trim(),
            jobTitle: values.jobTitle.trim(),
            company: values.company.trim()
        })

        return Object.fromEntries(fields.filter(field => all[field]).map(field => [field, all[field]])) as FieldErrors<DemoField>
    }

    const next = () => {
        const found = errorsFor(DEMO_STEPS[step])

        if (Object.keys(found).length) {
            setErrors(found)
            focusFirstError('demo', DEMO_STEPS[step].filter(field => found[field]))

            return
        }

        setErrors({})
        setStep(current => Math.min(current + 1, DEMO_STEPS.length - 1))
    }

    const back = () => {
        setMessage(null)
        setStep(current => Math.max(0, current - 1))
    }

    const onSubmit = async (event: FormEvent<HTMLFormElement>) => {
        event.preventDefault()

        if (step < DEMO_STEPS.length - 1) {
            next()

            return
        }

        setMessage(null)

        const found = errorsFor(DEMO_STEPS.flat())

        if (Object.keys(found).length) {
            setErrors(found)

            const firstStep = DEMO_STEPS.findIndex(fields => fields.some(field => found[field]))

            setStep(firstStep >= 0 ? firstStep : 0)
            setMessage('A few details need another look before we can send your request.')

            return
        }

        setStatus('submitting')

        const result = await postJson('/api/demo', { ...values, website })

        if (result.ok) {
            setStatus('success')

            return
        }

        setStatus('idle')
        setMessage(result.message ?? null)

        if (result.errors) {
            const serverErrors = result.errors as FieldErrors<DemoField>

            setErrors(serverErrors)

            const firstStep = DEMO_STEPS.findIndex(fields => fields.some(field => serverErrors[field]))

            if (firstStep >= 0) setStep(firstStep)
        }
    }

    if (status === 'success') {
        return (
            <div role='status' className='py-4'>
                <span className='flex size-14 items-center justify-center rounded-full bg-sage/25 text-sage-dark'>
                    <CircleCheck aria-hidden='true' className='size-7' />
                </span>

                <h2 ref={headingRef} tabIndex={-1} className='mt-8 font-heading text-display-sm font-medium text-burgundy outline-none'>
                    Request received, {values.firstName.trim()}.
                </h2>

                <p className='mt-4 max-w-md leading-relaxed text-stone-dark'>
                    Thank you for your interest in Veyra. Here is what happens next for {values.company.trim()}.
                </p>

                <ol className='mt-10 space-y-6'>
                    {NEXT_STEPS.map(({ title, body, Icon }, index) => (
                        <li key={title} className='flex gap-4'>
                            <span className='flex size-10 shrink-0 items-center justify-center rounded-full bg-burgundy text-gold'>
                                <Icon aria-hidden='true' className='size-4' />
                            </span>

                            <span>
                                <span className='block font-mono text-[10.5px] uppercase tracking-[0.18em] text-stone'>Step {index + 1}</span>
                                <span className='mt-1 block font-medium text-burgundy'>{title}</span>
                                <span className='mt-1 block text-sm leading-relaxed text-stone-dark'>{body}</span>
                            </span>
                        </li>
                    ))}
                </ol>

                <div className='mt-10 flex flex-wrap gap-3'>
                    <Link href='/resources' className='inline-flex h-11 items-center rounded-full border border-burgundy/20 px-5 text-sm font-medium text-burgundy transition-colors hover:border-burgundy/40'>
                        Browse resources
                    </Link>

                    <Link href='/' className='inline-flex h-11 items-center rounded-full px-5 text-sm font-medium text-stone-dark transition-colors hover:text-burgundy'>
                        Back to home
                    </Link>
                </div>
            </div>
        )
    }

    return (
        <form noValidate onSubmit={onSubmit} className='relative'>
            <Honeypot value={website} onChange={setWebsite} />

            <ol aria-label='Progress' className='grid grid-cols-3 gap-2'>
                {STEP_LABELS.map((label, index) => (
                    <li key={label} aria-current={index === step ? 'step' : undefined}>
                        <span className={cn('block h-1 rounded-full transition-colors duration-500', index <= step ? 'bg-burgundy' : 'bg-burgundy/10')} />

                        <span className={cn('mt-3 block text-xs font-medium', index === step ? 'text-burgundy' : 'text-stone')}>
                            <span className='font-mono'>{String(index + 1).padStart(2, '0')}</span>
                            <span className='ml-2 hidden sm:inline'>{label}</span>
                        </span>
                    </li>
                ))}
            </ol>

            <h2 ref={headingRef} tabIndex={-1} className='mt-10 font-heading text-2xl font-medium text-burgundy outline-none'>
                {STEP_LABELS[step]}
                <span className='sr-only'>, step {step + 1} of {STEP_LABELS.length}</span>
            </h2>

            {message && <FormAlert tone='error' className='mt-6'>{message}</FormAlert>}

            <div key={step} className='mt-8 animate-in space-y-6 fade-in slide-in-from-right-3 duration-500'>
                {step === 0 && (
                    <>
                        <div className='grid gap-6 sm:grid-cols-2'>
                            <TextField id='demo-firstName' label='First name' autoComplete='given-name' value={values.firstName} onChange={event => update('firstName', event.target.value)} error={errors.firstName} />
                            <TextField id='demo-lastName' label='Last name' autoComplete='family-name' value={values.lastName} onChange={event => update('lastName', event.target.value)} error={errors.lastName} />
                        </div>

                        <TextField id='demo-email' label='Work email' type='email' inputMode='email' autoComplete='email' value={values.email} onChange={event => update('email', event.target.value)} error={errors.email} />
                        <TextField id='demo-jobTitle' label='Job title' autoComplete='organization-title' placeholder='For example, Head of Finance' value={values.jobTitle} onChange={event => update('jobTitle', event.target.value)} error={errors.jobTitle} />
                    </>
                )}

                {step === 1 && (
                    <>
                        <TextField id='demo-company' label='Company name' autoComplete='organization' value={values.company} onChange={event => update('company', event.target.value)} error={errors.company} />

                        <div className='grid gap-6 sm:grid-cols-2'>
                            <SelectField id='demo-companySize' label='Company size' placeholder='Choose a size' options={COMPANY_SIZES} value={values.companySize} onChange={event => update('companySize', event.target.value)} error={errors.companySize} />
                            <SelectField id='demo-industry' label='Industry' optional placeholder='Choose an industry' options={INDUSTRIES} value={values.industry} onChange={event => update('industry', event.target.value)} error={errors.industry} />
                        </div>

                        <ChipGroup
                            id='demo-tools'
                            label='What do you use today?'
                            options={CURRENT_TOOLS}
                            value={values.tools}
                            onChange={tools => update('tools', tools)}
                            hint='Select all that apply. We will tailor the walkthrough to your stack.'
                            error={errors.tools}
                        />
                    </>
                )}

                {step === 2 && (
                    <>
                        <TextAreaField
                            id='demo-goals'
                            label='What would you like to improve?'
                            optional
                            rows={5}
                            placeholder='For example, closing faster, forecasting cash or controlling spend across teams.'
                            value={values.goals}
                            onChange={event => update('goals', event.target.value)}
                            error={errors.goals}
                        />

                        <div className='grid gap-6 sm:grid-cols-2'>
                            <SelectField id='demo-planInterest' label='Plan you are considering' options={PLAN_INTERESTS} value={values.planInterest} onChange={event => update('planInterest', event.target.value)} error={errors.planInterest} />
                            <SelectField id='demo-preferredTime' label='Best time for a call' options={MEETING_TIMES} value={values.preferredTime} onChange={event => update('preferredTime', event.target.value)} error={errors.preferredTime} />
                        </div>

                        <div className='rounded-2xl border border-burgundy/10 bg-ivory p-5 text-sm'>
                            <p className='font-mono text-[10.5px] uppercase tracking-[0.18em] text-stone'>Your request</p>

                            <p className='mt-2 text-burgundy'>
                                <span className='font-medium'>{`${values.firstName} ${values.lastName}`.trim() || 'Your name'}</span>
                                {values.jobTitle && <span className='text-stone-dark'>, {values.jobTitle}</span>}
                            </p>

                            <p className='mt-1 text-stone-dark'>
                                {[values.company, values.companySize, values.email].filter(Boolean).join('  /  ')}
                            </p>
                        </div>
                    </>
                )}
            </div>

            <div className='mt-10 flex items-center justify-between gap-4 border-t border-burgundy/10 pt-6'>
                {step > 0 ? (
                    <button type='button' onClick={back} className='inline-flex items-center gap-2 text-sm font-medium text-stone-dark transition-colors hover:text-burgundy'>
                        <ArrowLeft aria-hidden='true' className='size-4' />
                        Back
                    </button>
                ) : (
                    <span className='text-xs text-stone'>Takes about a minute</span>
                )}

                <Button type='submit' size='lg' arrow loading={status === 'submitting'}>
                    {step < DEMO_STEPS.length - 1 ? 'Continue' : status === 'submitting' ? 'Sending' : 'Request demo'}
                </Button>
            </div>
        </form>
    )
}
